import { cssVar, _deepClone } from "nvd-js-helpers/misc";
import {
    normalizeConnectionRoute,
    setConnectionRoute,
} from "@/modules/canvas/connection-routing.js";
import { toRaw } from "vue";

function sortKeysDeep(value) {
    if (value === null || typeof value !== 'object') return value
    if (Array.isArray(value)) return value.map(sortKeysDeep)
    const sorted = {}
    for (const k of Object.keys(value).sort()) {
        sorted[k] = sortKeysDeep(value[k])
    }
    return sorted
}

/** Align field shapes for dirty-check (controls coerce undefined/booleans; FK UI uses '' vs null). */
function fieldComparable(field) {
    const m = { ...newField, ...field }
    for (const k of ['fk_field', 'fk_table', 'rel_type', 'color', 'size', 'default_value', 'comment']) {
        if (m[k] == null) m[k] = ''
    }
    return sortKeysDeep(m)
}

function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const newField = {
    "name": "new_field",
    "type": "integer",
    "size": "",
    "default_value": "",
    "allow_null": false,
    "unique": false,
    "fk": false,
    "auto_increment": false,
    "pk": false,
    "order": "",
    "p_id": "new",
    "resource": 'field',
    "fk_table": '',
    "fk_field": '',
    "rel_type": '',
    "connection_route": null,
    "color": '',
    "parent_resource": 'table',
    "checked": true,
    "idx": '',
    "length": '',
}

export const newIndex = {
    id: '',
    name: '',
    columns: '',
    unique: false,
    method: '',
}

export const newConstraint = {
    id: '',
    name: '',
    type: 'primary_key',
    definition: '',
}

function normalizeIndex(index = {}) {
    return {
        ...newIndex,
        ...index,
    }
}

function normalizeConstraint(constraint = {}) {
    return {
        ...newConstraint,
        ...constraint,
    }
}

export function normalizeTable(table = {}) {
    return {
        ...table,
        indexes: Array.isArray(table.indexes) ? table.indexes.map(normalizeIndex) : [],
        constraints: Array.isArray(table.constraints) ? table.constraints.map(normalizeConstraint) : [],
    }
}

/** Canvas / UI: whether a field name appears in table indexes or constraint definitions. */
export function fieldSchemaMarkers(fieldName, table = {}) {
    const t = normalizeTable(table)
    const name = String(fieldName || '').trim().toLowerCase()
    const empty = { inIndex: false, inConstraint: false }
    if (!name) return empty

    const inIndex = (t.indexes || []).some((ix) => {
        const cols = String(ix.columns || '')
            .split(',')
            .map((c) => c.trim().toLowerCase())
            .filter(Boolean)
        return cols.includes(name)
    })

    const inConstraint = (t.constraints || []).some((c) => {
        const def = c.definition || ''
        if (!def.trim()) return false
        const re = new RegExp(`\\b${escapeRegExp(String(fieldName).trim())}\\b`, 'i')
        return re.test(def)
    })

    return { inIndex, inConstraint }
}

/** Stable string for comparing edit-modal state (after the same normalization as save). */
export function tableComparableSnapshot(table) {
    const raw = table && typeof table === 'object' ? toRaw(table) : table
    if (!raw || typeof raw !== 'object') return ''
    const t = normalizeTable(_deepClone(raw))
    sanitizeTableMetadata(t)
    const payload = {
        name: t.name,
        color: t.color || '',
        comment: t.comment || '',
        fields: (t.fields || []).map(fieldComparable),
        indexes: (t.indexes || []).map((x) => sortKeysDeep({ ...newIndex, ...x })),
        constraints: (t.constraints || []).map((c) => sortKeysDeep({ ...newConstraint, ...c })),
    }
    return JSON.stringify(sortKeysDeep(payload))
}

export function sanitizeTableMetadata(table = {}) {
    table.fields = (table.fields || []).map(field => {
        if (!field.fk || !field.fk_table || !field.fk_field) {
            if (Object.prototype.hasOwnProperty.call(field, 'connection_route')) {
                setConnectionRoute(field, null)
            }
            return field
        }

        const route = normalizeConnectionRoute(field.connection_route)
        if (route) {
            field.connection_route = route
        } else if (Object.prototype.hasOwnProperty.call(field, 'connection_route')) {
            setConnectionRoute(field, null)
        }
        return field
    })

    table.indexes = (table.indexes || [])
        .map(normalizeIndex)
        .map(index => ({
            ...index,
            name: index.name?.trim?.() || '',
            columns: index.columns?.trim?.() || '',
            method: index.method?.trim?.() || '',
        }))
        .filter(index => index.name || index.columns || index.method || index.unique)

    table.constraints = (table.constraints || [])
        .map(normalizeConstraint)
        .map(constraint => ({
            ...constraint,
            name: constraint.name?.trim?.() || '',
            type: constraint.type?.trim?.() || newConstraint.type,
            definition: constraint.definition?.trim?.() || '',
        }))
        .filter(constraint => constraint.name || constraint.definition)

    return table
}

export function clearConnectionRoutes(table = {}) {
    for (const field of table.fields || []) {
        delete field.connection_route
    }
    return table
}

export function newTable(table = {}) {
    return normalizeTable({
        p_id: "new",
        name: "Untitled",
        comment: "",
        color: cssVar('--primary'),
        fields: [],
        resource: "table",
        checked: true,
        ...table,
    })
}

export const TableHelper = {
    nameToId(name, jsp) {
        return jsp.getContainer().id + '-' + name.toLowerCase().replace(/[^a-zA-Z\d]/g, '_')
    },
    fieldElementId(tableName, fieldName, jsp) {
        return `${this.nameToId(tableName, jsp)}-${this.nameToId(fieldName, jsp)}`
    },
}
